import { Property } from '@go-shared/types/property';
import { Template, TemplateTransferTask } from '@go-shared/types/template';
import React, { useEffect, useState } from 'react';

import { BubbleForm, BubbleOverlay } from '../BubbleUI';
import { BubbleFormPage } from '../BubbleUI/BubbleForm';
import { User } from '@go-shared/types/user';
import { TmplTransferEvtPage } from './TmplTransferEvtPage';
import { TmplTransferCntPage } from './TmplTransferCntPage';
import { TmplTransferVenPage } from './TmplTransferVenPage';
import { TmplTransferPropPage } from './TmplTransferPropPage';
import { BareEventTemplate, StrategicLibraryTransfer } from '@go-shared/types/eventTemplate';
import { getPropertyEventTemplates } from '@go-client/lib/api/eventTemplate';
import { hasOwnProperty, safeIdCompare } from '@go-shared/lib/utils';
import { notUndefined, onlyUnique } from '@go-shared/lib/functional';
import { BareContentTemplate } from '@go-shared/types/contentTemplate';
import { getPropertyContentTemplates } from '@go-client/lib/api/contentTemplate';
import { BareFeedTemplate } from '@go-shared/types/feedTemplate';
import { findFeedAreas } from '@go-shared/lib/feed';
import { getVenueTemplatesByPropertyId } from '@go-client/lib/api/venueTemplate';
import { BareVenueTemplate } from '@go-shared/types/venueTemplate';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import Fuse from 'fuse.js';
import { transferTemplate } from '@go-client/lib/api/template';
import { TmplTransferImgPage } from './TmplTransferImgPage';

export interface TemplateTransferFormProps {
  property: Property;
  user: User;
  template: Template;
  onClose?: () => void;
}

export type Promotable<T> = { item: T; strategy?: StrategicLibraryTransfer };
export type Targettable<T> = { item: T; target: T | undefined };

export interface TransferForm {
  templateName: string;
  target: Property | undefined;
  source: Property | undefined;
  eventsFix: Array<Promotable<BareEventTemplate>>;
  contentsFix: Array<Promotable<BareContentTemplate>>;
  venueMap: Array<Targettable<BareVenueTemplate>>;
  bgImagesDict: { [key: string]: string | undefined };
  _dirty: boolean;
}

function generateDefaultTransferForm(template: Template): TransferForm {
  return {
    templateName: template.name,
    target: undefined,
    source: undefined,
    eventsFix: [],
    contentsFix: [],
    venueMap: [],
    bgImagesDict: {
      ...(template.feeds ?? [])
        .flatMap(f => f.pages ?? [])
        .reduce(
          (acc, page) => {
            acc[page._id] = page.image;
            return acc;
          },
          {} as Record<string, string | undefined>
        ),
    },
    _dirty: false,
  };
}

interface TemplateTransferFormDataBundle {
  eventTemplates: BareEventTemplate[];
  contentTemplates: BareContentTemplate[];
  venueTemplates: BareVenueTemplate[];
}

export default function TemplateTransferForm(props: TemplateTransferFormProps) {
  const [dataBundle, setDataBundle] = useState<TemplateTransferFormDataBundle | undefined>(
    undefined
  );
  const [targetVenues, setTargetVenues] = useState<BareVenueTemplate[]>([]);
  const [transferForm, setTransferForm] = useState<TransferForm>(
    generateDefaultTransferForm(props.template)
  );
  const [isBusy, setIsBusy] = useState(false);

  function changeTransferForm<T extends keyof TransferForm>(key: T, val: TransferForm[T]) {
    setTransferForm({ ...transferForm, _dirty: true, [key]: val });
  }

  useEffect(() => {
    fetchDataBundle().catch(console.error);
  }, [props.property]);

  async function fetchDataBundle() {
    const [eventTemplates, contentTemplates, venueTemplates] = await Promise.all([
      getPropertyEventTemplates(props.property._id),
      getPropertyContentTemplates(props.property._id),
      getVenueTemplatesByPropertyId(props.property._id),
    ]);

    setDataBundle({ eventTemplates, contentTemplates, venueTemplates });
  }

  function extractContent(feeds: BareFeedTemplate[]): string[] {
    return feeds
      .map(feed => {
        const columns = findFeedAreas(feed, 'column');
        return columns
          .map(c => {
            if (!c.content) return undefined;

            return hasOwnProperty(c.content, 'name') &&
              hasOwnProperty(c.content, 'content') &&
              c.content.name === 'Custom'
              ? (c.content.content as string)
              : undefined;
          })
          .filter(notUndefined);
      })
      .flat();
  }

  const evtsInPlanSet = new Set(props.template.plan.events.map(e => e.template).filter(onlyUnique));
  const cntInPlanSet = new Set(extractContent(props.template.feeds || []).filter(onlyUnique));

  useEffect(() => {
    resetForm();
  }, [transferForm.target, dataBundle, targetVenues]);

  function resetForm() {
    if (!transferForm.target || !dataBundle) return;

    const fuse = new Fuse(targetVenues, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['name'],
    });

    setTransferForm({
      ...transferForm,
      venueMap: dataBundle.venueTemplates
        .filter(v => !v.archived)
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
        .map(v => ({
          item: v,
          target: fuse.search(v.name)[0]?.item,
        })),
      eventsFix: dataBundle.eventTemplates
        .filter(
          et =>
            et.source &&
            et.properties.findIndex(p => safeIdCompare(p.property, transferForm.target?._id)) < 0 &&
            evtsInPlanSet.has(et._id)
        )
        .map(itm => ({
          item: itm,
          strategy: 'promote-to-brand',
        })),
      contentsFix: dataBundle.contentTemplates
        .filter(
          ct =>
            ct.source &&
            ct.properties.findIndex(p => safeIdCompare(p.property, transferForm.target?._id)) < 0 &&
            cntInPlanSet.has(ct._id)
        )
        .map(itm => ({
          item: itm,
          strategy: 'promote-to-brand',
        })),
    });
  }

  useEffect(() => {
    fetchPropertyInfo().catch(console.error);
  }, [transferForm.target]);

  async function fetchPropertyInfo() {
    if (!transferForm.target) return;

    try {
      setIsBusy(true);
      const targetVenues = await getVenueTemplatesByPropertyId(transferForm.target._id);
      targetVenues.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      setTargetVenues(targetVenues);
    } finally {
      setIsBusy(false);
    }
  }

  async function handleFinishTransferTemplates() {
    if (!transferForm.target) return;

    try {
      setIsBusy(true);
      const task: TemplateTransferTask = {
        templateId: props.template._id,
        templateName: transferForm.templateName || props.template.name,
        targetProperty: transferForm.target._id,
        eventsFix: transferForm.eventsFix.map(e => ({ eventId: e.item._id, strategy: e.strategy })),
        contentsFix: transferForm.contentsFix.map(c => ({
          contentId: c.item._id,
          strategy: c.strategy,
        })),
        venuesMap: transferForm.venueMap.map(v => ({ fromId: v.item._id, toId: v.target?._id })),
        bgImagesMap: Object.keys(transferForm.bgImagesDict)
          .map(item => {
            const val = transferForm.bgImagesDict[item];
            return val
              ? {
                  pageId: item,
                  newSrc: val,
                }
              : undefined;
          })
          .filter(notUndefined),
      };
      await transferTemplate(props.property._id, task);
      if (props.onClose) props.onClose();
    } finally {
      setIsBusy(false);
    }
  }

  const pages: Array<BubbleFormPage<unknown>> = [
    {
      key: 'page-1',
      render: actions => (
        <TmplTransferPropPage
          template={props.template}
          transferForm={transferForm}
          changeTransferForm={changeTransferForm}
          isBusy={isBusy}
          onClose={props.onClose}
          onGoNext={actions.goForward}
        />
      ),
    },
    {
      key: 'page-2',
      render: actions => (
        <TmplTransferEvtPage
          transferForm={transferForm}
          changeTransferForm={changeTransferForm}
          template={props.template}
          isBusy={isBusy}
          onClose={props.onClose}
          onGoNext={actions.goForward}
          onGoBack={actions.goBack}
        />
      ),
    },
    {
      key: 'page-3',
      render: actions => (
        <TmplTransferVenPage
          transferForm={transferForm}
          targetVenues={targetVenues}
          changeTransferForm={changeTransferForm}
          template={props.template}
          isBusy={isBusy}
          onClose={props.onClose}
          onGoNext={actions.goForward}
          onGoBack={actions.goBack}
        />
      ),
    },
    {
      key: 'page-4',
      render: actions => (
        <TmplTransferCntPage
          transferForm={transferForm}
          changeTransferForm={changeTransferForm}
          template={props.template}
          isBusy={isBusy}
          onClose={props.onClose}
          onGoNext={actions.goForward}
          onGoBack={actions.goBack}
        />
      ),
    },
    {
      key: 'page-5',
      render: actions => (
        <TmplTransferImgPage
          transferForm={transferForm}
          changeTransferForm={changeTransferForm}
          isBusy={isBusy}
          template={props.template}
          onClose={props.onClose}
          onFinishTransferTemplates={handleFinishTransferTemplates}
          onGoBack={actions.goBack}
        />
      ),
    },
  ];

  return (
    <div className="template-transfer-form">
      {!dataBundle ? (
        <BubbleOverlay hideBg loadingMinHeight="large" loading={!dataBundle} />
      ) : (
        <BubbleForm pages={pages} />
      )}
    </div>
  );
}
